// FbBuyerDash.jsx — /fb-comments: Facebook buyers and what they comment, styled and
// behaving like /sellerdash (NdSellerDash.jsx).
//
// Shows ONLY comments the AI approved (fbBuyerVetter.js: aiVerdict 'good'). A buyer is
// one person, so a row is one person: their best comment up top and the rest behind
// "more". The older page with the listings, captions and group tools is at
// /fb-comments-classic.
import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Paper,
  Grid,
  Chip,
  LinearProgress,
  Avatar,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { collection, query, orderBy, limit, onSnapshot, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import dayjs from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import GroupsIcon from '@mui/icons-material/Groups';

import { db } from './firebase';
import { getPresetRange } from './NdSellerLeadsDashboard';
import {
  TIER_META,
  PRESET_LABELS,
  TierChip,
  StatCard,
  fmtWhen,
  tsToDate,
  lightToggleSx,
  darkToggleSx,
  feedPaperSx,
  pillButtonSx,
} from './NdSellerDash';
import logo2 from './leadfeed.png';

const PAGE_SIZE = 100;
const RUNS_TO_LOAD = 120;
// A sync run takes ~25 min and Cloud Run caps it at an hour.
const RUN_STALE_MS = 60 * 60 * 1000;

const RUN_STATUS_META = {
  success: { label: 'Done',    color: '#86efac', bg: 'rgba(34,197,94,0.18)' },
  partial: { label: 'Partial', color: '#fde68a', bg: 'rgba(250,204,21,0.16)' },
  running: { label: 'Running', color: '#7dd3fc', bg: 'rgba(56,189,248,0.16)' },
  hung:    { label: 'Hung',    color: '#fca5a5', bg: 'rgba(239,68,68,0.18)' },
  failed:  { label: 'Failed',  color: '#fca5a5', bg: 'rgba(239,68,68,0.18)' },
};

function runHealth(run, nowMs) {
  if (run.status !== 'running') return RUN_STATUS_META[run.status] ? run.status : 'failed';
  const started = tsToDate(run.startedAt);
  return started && nowMs - started.getTime() > RUN_STALE_MS ? 'hung' : 'running';
}

// commentAt is the real moment, saved at scrape time. commentTime is Facebook's text
// in the scraper's timezone (UTC in the cloud), so it's only a fallback, then scrapedAt.
function commentDate(c) {
  const at = tsToDate(c.commentAt);
  if (at) return at;
  const d = new Date(String(c.commentTime || '').replace(' at ', ' '));
  return Number.isNaN(d.getTime()) ? tsToDate(c.scrapedAt) : d;
}

// One key per Facebook person, however their profile link is written.
function personKey(c) {
  const url = (c.profileUrl || '').trim();
  if (url) {
    try {
      const u = new URL(url, 'https://www.facebook.com');
      const id = u.searchParams.get('id');
      if (id) return `id:${id}`;
      const user = u.pathname.match(/\/user\/(\d+)/);
      return `path:${user ? user[1] : u.pathname.replace(/\/+$/, '').toLowerCase()}`;
    } catch { /* fall through to the name */ }
  }
  return `name:${(c.name || '').trim().toLowerCase()}`;
}

// Approved comments in the window → one row per buyer, best comment first (score,
// then newest). A buyer is in the window when any of their approved comments is.
function buildBuyers(comments, from, to) {
  const byPerson = new Map();
  comments.forEach((c) => {
    if (c.aiVerdict !== 'good' || c.hidden === true) return;
    const date = commentDate(c);
    if (from && (!date || date < from)) return;
    if (to && (!date || date >= to)) return;
    const key = personKey(c);
    if (!byPerson.has(key)) byPerson.set(key, []);
    byPerson.get(key).push({ ...c, date });
  });

  // DMed is per person: any flagged comment marks them, wherever it was.
  const dmedKeys = new Set();
  comments.forEach((c) => { if (c.dmed) dmedKeys.add(personKey(c)); });

  return [...byPerson.entries()].map(([key, list]) => {
    list.sort((a, b) => (b.aiScore - a.aiScore) || ((b.date?.getTime() || 0) - (a.date?.getTime() || 0)));
    const best = list[0];
    return {
      id: key,
      name: best.name,
      profilePhoto: best.profilePhoto,
      profileUrl: best.profileUrl,
      best,
      comments: list,
      latest: list.reduce((m, c) => (c.date && (!m || c.date > m) ? c.date : m), null),
      dmed: dmedKeys.has(key),
    };
  });
}

function compareBuyers(a, b) {
  if (b.best.aiScore !== a.best.aiScore) return b.best.aiScore - a.best.aiScore;
  return (b.latest?.getTime() || 0) - (a.latest?.getTime() || 0);
}

const fmtComment = (d) =>
  (d ? d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '');

function BuyerRow({ buyer, rank, allComments }) {
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState(false);
  const { best } = buyer;
  const others = buyer.comments.length - 1;
  const initials =
    (buyer.name || '')
      .split(' ')
      .filter(Boolean)
      .map((p) => p[0])
      .join('')
      .slice(0, 2) || '?';

  // Same behavior as the classic page: flag one comment, clear them all.
  async function toggleDmed() {
    setBusy(true);
    try {
      if (buyer.dmed) {
        const flagged = allComments.filter((c) => c.dmed && personKey(c) === buyer.id);
        await Promise.all(flagged.map((c) => updateDoc(doc(db, 'fbGroupComments', c.id), { dmed: false, dmedAt: null })));
      } else {
        await updateDoc(doc(db, 'fbGroupComments', best.id), { dmed: true, dmedAt: serverTimestamp() });
      }
    } catch (e) {
      console.error('[fb-comments] DM flag failed', e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, py: 1 }}>
      {rank != null && (
        <Typography
          sx={{
            minWidth: 40,
            flexShrink: 0,
            pt: 0.75,
            textAlign: 'right',
            fontWeight: 800,
            fontSize: 17,
            color: rank <= 3 ? '#facc15' : '#64748b',
          }}
        >
          #{rank}
        </Typography>
      )}
      <Avatar
        src={buyer.profilePhoto || undefined}
        alt={buyer.name || 'Buyer'}
        sx={{
          width: 40,
          height: 40,
          bgcolor: buyer.profilePhoto ? '#111827' : 'rgba(16,185,129,0.9)',
          fontSize: 14,
          fontWeight: 600,
          border: '1px solid rgba(31,41,55,1)',
        }}
      >
        {!buyer.profilePhoto ? initials : null}
      </Avatar>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        {/* Header row */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 1,
            mb: 0.5,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" fontWeight={600} noWrap sx={{ color: '#ffffff' }}>
              {buyer.name || 'Unknown'}
            </Typography>
            <Typography variant="caption" sx={{ color: '#94a3b8' }}>
              Commented {fmtComment(best.date)}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            {/* Group pill */}
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                px: 1,
                py: 0.3,
                borderRadius: 9999,
                bgcolor: 'rgba(15,23,42,1)',
                border: '1px solid rgba(55,65,81,0.95)',
                maxWidth: 320,
              }}
            >
              <GroupsIcon sx={{ fontSize: 16, color: '#22c55e' }} />
              <Typography variant="caption" noWrap sx={{ color: '#ffffff', fontWeight: 500 }}>
                {best.groupName || '—'}
              </Typography>
            </Box>

            {buyer.dmed && (
              <Chip
                size="small"
                label="DMed"
                sx={{ height: 22, fontSize: 11, fontWeight: 700, bgcolor: 'rgba(56,189,248,0.16)', color: '#7dd3fc' }}
              />
            )}

            <TierChip tier={best.aiTier} score={best.aiScore} />
          </Box>
        </Box>

        {/* What they said */}
        <Typography variant="body2" sx={{ whiteSpace: 'pre-line', color: '#e5e7eb', lineHeight: 1.5 }}>
          {best.comment || '—'}
        </Typography>

        {best.aiReason && (
          <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: '#86efac' }}>
            AI: {best.aiReason}
          </Typography>
        )}

        {/* Their other approved comments */}
        {expanded && buyer.comments.slice(1).map((c) => (
          <Box key={c.id} sx={{ mt: 1, pl: 1.5, borderLeft: '2px solid rgba(55,65,81,0.95)' }}>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-line', color: '#e5e7eb', lineHeight: 1.5 }}>
              {c.comment}
            </Typography>
            <Typography variant="caption" sx={{ color: '#64748b' }}>
              {fmtComment(c.date)} · {c.groupName}
              {(c.commentUrl || c.postUrl) && (
                <Box
                  component="a"
                  href={c.commentUrl || c.postUrl}
                  target="_blank"
                  rel="noreferrer"
                  sx={{ ml: 0.75, color: '#22c55e', textDecoration: 'none', fontWeight: 700 }}
                >
                  OPEN ↗
                </Box>
              )}
            </Typography>
          </Box>
        ))}

        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mt: 0.75 }}>
          <Button
            size="small"
            variant="outlined"
            disabled={busy}
            onClick={toggleDmed}
            sx={{ ...pillButtonSx, fontSize: 12, px: 1.5, py: 0.1 }}
          >
            {buyer.dmed ? 'Unmark DMed' : 'Mark DMed'}
          </Button>
          {others > 0 && (
            <Typography
              variant="caption"
              onClick={() => setExpanded((x) => !x)}
              sx={{ color: '#7dd3fc', cursor: 'pointer', fontWeight: 600 }}
            >
              {expanded ? '▾ hide' : `▸ ${others} more comment${others === 1 ? '' : 's'}`}
            </Typography>
          )}
          <Box sx={{ ml: 'auto', display: 'flex', gap: 1.5 }}>
            {buyer.profileUrl && (
              <Typography
                component="a"
                href={buyer.profileUrl}
                target="_blank"
                rel="noreferrer"
                variant="caption"
                sx={{ color: '#22c55e', textDecoration: 'none', fontWeight: 700 }}
              >
                PROFILE ↗
              </Typography>
            )}
            {(best.commentUrl || best.postUrl) && (
              <Typography
                component="a"
                href={best.commentUrl || best.postUrl}
                target="_blank"
                rel="noreferrer"
                variant="caption"
                sx={{ color: '#22c55e', textDecoration: 'none', fontWeight: 700 }}
              >
                COMMENT ↗
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

function BuyerList({ buyers, visible, setVisible, emptyText, allComments }) {
  if (!buyers.length) {
    return (
      <Typography variant="body2" sx={{ color: '#f9fafb' }}>
        {emptyText}
      </Typography>
    );
  }
  return (
    <Box>
      {buyers.slice(0, visible).map((b, idx) => (
        <Box key={b.id}>
          {idx > 0 && <Divider sx={{ my: 1, borderColor: 'rgba(31,41,55,1)' }} />}
          <BuyerRow buyer={b} rank={b.rank} allComments={allComments} />
        </Box>
      ))}

      {buyers.length > visible && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Button variant="outlined" onClick={() => setVisible((n) => n + PAGE_SIZE)} sx={pillButtonSx}>
            Show more ({buyers.length - visible} left)
          </Button>
        </Box>
      )}
    </Box>
  );
}

function TierCards({ total, totalTitle, totalSub, counts, tier, setTier, loading }) {
  return (
    <Grid container spacing={2} sx={{ mb: 3 }}>
      <Grid item xs={12} sm={6} md={3}>
        <StatCard
          accent="#38bdf8"
          title={totalTitle}
          value={loading ? '—' : total}
          sub={totalSub}
          active={tier === 'all'}
          hint={tier === 'all' ? '▾ showing all' : '▸ show all'}
          onClick={() => setTier('all')}
        />
      </Grid>
      {['hot', 'warm', 'maybe'].map((k) => (
        <Grid item xs={12} sm={6} md={3} key={k}>
          <StatCard
            accent={TIER_META[k].accent}
            title={`${TIER_META[k].label} · score ${TIER_META[k].range}`}
            value={loading ? '—' : counts[k]}
            sub={TIER_META[k].blurb}
            active={tier === k}
            hint={tier === k ? `▾ showing ${k} only` : `▸ show ${k} only`}
            onClick={() => setTier((cur) => (cur === k ? 'all' : k))}
          />
        </Grid>
      ))}
    </Grid>
  );
}

function countTiers(buyers) {
  const c = { hot: 0, warm: 0, maybe: 0 };
  buyers.forEach((b) => { if (c[b.best.aiTier] != null) c[b.best.aiTier] += 1; });
  return c;
}

export default function FbBuyerDash() {
  const [view, setView] = useState('top'); // 'top' | 'feed'
  const [preset, setPreset] = useState('all');
  const [dateMode, setDateMode] = useState('single'); // 'single' | 'range'
  const [singleDate, setSingleDate] = useState(dayjs());
  const [rangeStart, setRangeStart] = useState(dayjs().subtract(6, 'day'));
  const [rangeEnd, setRangeEnd] = useState(dayjs());

  const [comments, setComments] = useState(null);
  const [runs, setRuns] = useState([]);
  const [error, setError] = useState('');
  const [tier, setTier] = useState('all'); // 'all' | 'hot' | 'warm' | 'maybe'
  const [showRuns, setShowRuns] = useState(false);
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [nowMs, setNowMs] = useState(Date.now());

  // Re-evaluate "Hung" runs without waiting for a snapshot.
  useEffect(() => {
    const t = setInterval(() => setNowMs(Date.now()), 30 * 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    return onSnapshot(
      collection(db, 'fbGroupComments'),
      (snap) => setComments(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => {
        console.error('[fb-comments]', err);
        setError(err.message || String(err));
      },
    );
  }, []);

  useEffect(() => {
    return onSnapshot(
      query(collection(db, 'fbCommentScrapeRuns'), orderBy('startedAt', 'desc'), limit(RUNS_TO_LOAD)),
      (snap) => setRuns(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => {
        console.error('[fb-comments]', err);
        setError(err.message || String(err));
      },
    );
  }, []);

  useEffect(() => { setVisible(PAGE_SIZE); }, [view, preset, tier, dateMode, singleDate, rangeStart, rangeEnd]);

  const loading = comments === null;

  const { filterStart, filterEnd, label } = useMemo(() => {
    if (dateMode === 'range') {
      // tolerate From > To rather than showing an empty window
      const [lo, hi] = rangeStart.isAfter(rangeEnd) ? [rangeEnd, rangeStart] : [rangeStart, rangeEnd];
      const s = lo.startOf('day');
      return {
        filterStart: s.toDate(),
        filterEnd: hi.startOf('day').add(1, 'day').toDate(), // exclusive end
        label: `${s.format('MMM D')} – ${hi.format('MMM D')}`,
      };
    }
    const s = singleDate.startOf('day');
    return { filterStart: s.toDate(), filterEnd: s.add(1, 'day').toDate(), label: s.format('MMM D, YYYY') };
  }, [dateMode, singleDate, rangeStart, rangeEnd]);

  const isToday = dateMode === 'single' && singleDate.isSame(dayjs(), 'day');
  const statLabel = dateMode === 'range' ? label : (isToday ? 'Today' : singleDate.format('MMM D'));

  const unvetted = useMemo(() => (comments || []).filter((c) => !c.aiVetted).length, [comments]);

  const presetRange = useMemo(() => getPresetRange(preset), [preset]);
  const presetLabel = PRESET_LABELS[preset];
  const ranked = useMemo(
    () => buildBuyers(comments || [], presetRange?.from, null)
      .sort(compareBuyers)
      .map((b, i) => ({ ...b, rank: i + 1 })),
    [comments, presetRange],
  );
  const feed = useMemo(
    () => buildBuyers(comments || [], filterStart, filterEnd)
      .sort((a, b) => (b.latest?.getTime() || 0) - (a.latest?.getTime() || 0)),
    [comments, filterStart, filterEnd],
  );

  const current = view === 'top' ? ranked : feed;
  const tierCounts = useMemo(() => countTiers(current), [current]);
  const shown = tier === 'all' ? current : current.filter((b) => b.best.aiTier === tier);

  // ── Scrape health ──────────────────────────────────────────────────────────
  const runRows = useMemo(
    () => runs.map((r) => ({ ...r, health: runHealth(r, nowMs), started: tsToDate(r.startedAt) })),
    [runs, nowMs],
  );
  const activeRun = runRows.find((r) => r.health === 'running');
  // One-group test runs say nothing about the daily scrape (same rule as fbScrapeAlert.js).
  const fullRuns = useMemo(() => runRows.filter((r) => (r.groupsTotal || 0) > 1), [runRows]);
  const latestDone = fullRuns.find((r) => r.health !== 'running');
  const lastGood = fullRuns.find((r) => r.health === 'success' || r.health === 'partial');
  const scraperDown = !activeRun && (latestDone?.health === 'failed' || latestDone?.health === 'hung');
  // Consecutive failures since the last run that worked.
  const failStreak = useMemo(() => {
    const i = fullRuns.findIndex((r) => r.health === 'success' || r.health === 'partial');
    const slice = i === -1 ? fullRuns : fullRuns.slice(0, i);
    return slice.filter((r) => r.health === 'failed' || r.health === 'hung').length;
  }, [fullRuns]);
  const windowRuns = useMemo(
    () => runRows.filter((r) => r.started && r.started >= filterStart && r.started < filterEnd),
    [runRows, filterStart, filterEnd],
  );
  const windowOk = windowRuns.filter((r) => r.health === 'success').length;
  const windowProblems = [
    ['hung', 'hung'],
    ['failed', 'failed'],
    ['partial', 'partial'],
  ]
    .map(([k, word]) => {
      const n = windowRuns.filter((r) => r.health === k).length;
      return n ? `${n} ${word}` : null;
    })
    .filter(Boolean);

  // ─────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────
  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        px: 2,
        py: 4,
        background:
          'radial-gradient(circle at 0% 0%, #1e293b 0, #020617 35%, #020617 60%, #020617 100%)',
        color: '#e5e7eb',
      }}
    >
      <Box sx={{ width: '100%', maxWidth: 1280 }}>
        {/* Top Center Logo */}
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', mb: 4 }}>
          <Box
            sx={{
              p: 2.5,
              borderRadius: 3,
              bgcolor: '#ffffff',
              border: '1px solid rgba(0,0,0,0.08)',
              boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
            }}
          >
            <img src={logo2} alt="LeadFeed" style={{ height: 70, display: 'block' }} />
          </Box>
        </Box>

        {/* Header */}
        <Paper
          elevation={0}
          sx={{
            mb: 3,
            px: 3,
            py: 2.5,
            borderRadius: 3,
            bgcolor: 'rgba(15,23,42,0.9)',
            border: '1px solid rgba(148,163,184,0.35)',
            boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
            backdropFilter: 'blur(18px)',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
              mb: 2.5,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                sx={{
                  p: 1,
                  borderRadius: 2,
                  bgcolor: '#ffffff',
                  border: '1px solid rgba(148,163,184,0.5)',
                  boxShadow: '0 0 30px rgba(34,197,94,0.25)',
                }}
              >
                <img src={logo2} alt="LeadFeed" style={{ height: 40, display: 'block' }} />
              </Box>
              <Box>
                <Typography
                  variant="h5"
                  fontWeight={800}
                  sx={{ letterSpacing: 0.3, textTransform: 'uppercase', color: '#f9fafb' }}
                >
                  FB Buyer Leads
                </Typography>
                <Typography variant="body2" sx={{ color: '#9ca3af', mt: 0.5 }}>
                  Facebook group comments · AI vetted
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
              {/* Live indicator: pulses during a scrape, red when the scraper is down */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    bgcolor: scraperDown ? '#ef4444' : '#22c55e',
                    boxShadow: scraperDown ? '0 0 15px rgba(239,68,68,0.9)' : '0 0 15px rgba(34,197,94,0.9)',
                    animation: activeRun ? 'pulseDot 1.4s ease-in-out infinite' : 'none',
                    '@keyframes pulseDot': {
                      '0%': { transform: 'scale(1)', opacity: 1 },
                      '50%': { transform: 'scale(1.6)', opacity: 0.5 },
                      '100%': { transform: 'scale(1)', opacity: 1 },
                    },
                  }}
                />
                <Typography variant="caption" sx={{ color: scraperDown ? '#fca5a5' : '#f9fafb' }}>
                  {activeRun
                    ? `Live · Scraping (${activeRun.groupsDone || 0}/${activeRun.groupsTotal || '?'} groups)`
                    : scraperDown ? 'Scraper down' : 'Live · Up to date'}
                </Typography>
              </Box>

              <Button variant="outlined" component={RouterLink} to="/fb-comments-classic" sx={pillButtonSx}>
                Listings & full browser
              </Button>
            </Box>
          </Box>

          {/* Date bar */}
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Paper
              elevation={0}
              sx={{
                px: 2.5,
                py: 2,
                borderRadius: 3,
                backgroundColor: '#f8fafc',
                boxShadow: '0 6px 20px rgba(0,0,0,0.06)',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 2,
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {view === 'top' ? (
                  <>
                    <ToggleButtonGroup
                      exclusive
                      value={preset}
                      onChange={(_, val) => val && setPreset(val)}
                      sx={lightToggleSx}
                    >
                      {Object.entries(PRESET_LABELS).map(([key, text]) => (
                        <ToggleButton key={key} value={key}>{text}</ToggleButton>
                      ))}
                    </ToggleButtonGroup>
                    <Chip
                      label="By comment date"
                      sx={{ backgroundColor: '#e2e8f0', color: '#334155', fontWeight: 700, borderRadius: 2 }}
                    />
                  </>
                ) : (
                  <>
                    <ToggleButtonGroup
                      exclusive
                      value={dateMode}
                      onChange={(_, val) => val && setDateMode(val)}
                      sx={lightToggleSx}
                    >
                      <ToggleButton value="single">One date</ToggleButton>
                      <ToggleButton value="range">Range</ToggleButton>
                    </ToggleButtonGroup>

                    {dateMode === 'single' ? (
                      <DatePicker
                        label="Date"
                        value={singleDate}
                        onChange={(v) => v && v.isValid() && setSingleDate(v)}
                        slotProps={{
                          textField: {
                            size: 'small',
                            sx: { minWidth: 160, backgroundColor: '#fff', borderRadius: 2 },
                          },
                        }}
                      />
                    ) : (
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
                        <DatePicker
                          label="From"
                          value={rangeStart}
                          onChange={(v) => v && v.isValid() && setRangeStart(v)}
                          slotProps={{
                            textField: {
                              size: 'small',
                              sx: { minWidth: 150, backgroundColor: '#fff', borderRadius: 2 },
                            },
                          }}
                        />
                        <DatePicker
                          label="To"
                          value={rangeEnd}
                          onChange={(v) => v && v.isValid() && setRangeEnd(v)}
                          slotProps={{
                            textField: {
                              size: 'small',
                              sx: { minWidth: 150, backgroundColor: '#fff', borderRadius: 2 },
                            },
                          }}
                        />
                      </Box>
                    )}

                    <Chip
                      label={label}
                      sx={{ backgroundColor: '#e2e8f0', color: '#334155', fontWeight: 700, borderRadius: 2 }}
                    />
                  </>
                )}
              </Box>
            </Paper>
          </LocalizationProvider>
        </Paper>

        {/* Scraper-down banner */}
        {scraperDown && (
          <Box
            sx={{
              mb: 3,
              px: 2.5,
              py: 1.5,
              borderRadius: 3,
              bgcolor: 'rgba(127,29,29,0.35)',
              border: '1px solid rgba(239,68,68,0.6)',
            }}
          >
            <Typography variant="body2" sx={{ color: '#fecaca', fontWeight: 700 }}>
              No new buyers are coming in: the last {failStreak >= RUNS_TO_LOAD ? `${RUNS_TO_LOAD}+` : failStreak} scrape{failStreak === 1 ? '' : 's'} failed.
              {lastGood?.started ? ` Last good run: ${fmtWhen(lastGood.started)}.` : ''}
            </Typography>
            {latestDone?.error && (
              <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: '#fca5a5' }}>
                {latestDone.error}
              </Typography>
            )}
          </Box>
        )}

        {/* View tabs */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
          <ToggleButtonGroup
            exclusive
            value={view}
            onChange={(_, val) => val && setView(val)}
            sx={{
              ...darkToggleSx,
              border: '1px solid rgba(55,65,81,0.95)',
              '& .MuiToggleButton-root': {
                ...darkToggleSx['& .MuiToggleButton-root'],
                fontSize: 14,
                fontWeight: 700,
                px: 3,
                py: 0.9,
              },
            }}
          >
            <ToggleButton value="top">🏆 Highest scoring</ToggleButton>
            <ToggleButton value="feed">🆕 Latest approved</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <TierCards
          loading={loading}
          total={current.length}
          totalTitle={view === 'top' ? `Ranked Buyers · ${presetLabel}` : `Approved · ${statLabel}`}
          totalSub={view === 'top' ? 'AI-approved, best first' : 'Buyers who commented in this window'}
          counts={tierCounts}
          tier={tier}
          setTier={setTier}
        />

        {/* Scrape health, one line (expands to the run list) */}
        {view === 'feed' && (
          <Box
            onClick={() => setShowRuns((x) => !x)}
            sx={{
              mb: 3,
              px: 2,
              py: 1,
              borderRadius: 2,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              flexWrap: 'wrap',
              bgcolor: 'rgba(15,23,42,0.9)',
              border: '1px solid rgba(59,130,246,0.35)',
            }}
          >
            <Typography variant="body2" sx={{ color: '#f9fafb', fontWeight: 600 }}>
              Scrape runs · {statLabel}: {windowOk}/{windowRuns.length} finished
            </Typography>
            <Typography variant="caption" sx={{ color: windowProblems.length ? '#fca5a5' : '#94a3b8' }}>
              {!windowRuns.length ? 'No runs' : windowProblems.length ? windowProblems.join(' · ') : 'all clean'}
            </Typography>
            <Typography variant="caption" sx={{ color: '#7dd3fc', ml: 'auto' }}>
              {showRuns ? '▾ hide runs' : '▸ show runs'}
            </Typography>
          </Box>
        )}

        {/* Run list behind the runs card */}
        {view === 'feed' && showRuns && (
          <Box
            sx={{
              mb: 3,
              borderRadius: 3,
              bgcolor: 'rgba(15,23,42,0.9)',
              border: '1px solid rgba(59,130,246,0.35)',
              overflow: 'hidden',
            }}
          >
            {!windowRuns.length && (
              <Typography variant="body2" sx={{ color: '#94a3b8', p: 2 }}>
                No scrape runs in this window.
              </Typography>
            )}
            {windowRuns.map((r) => {
              const m = RUN_STATUS_META[r.health];
              return (
                <Box
                  key={r.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 1.5,
                    px: 2,
                    py: 1.25,
                    borderBottom: '1px solid rgba(148,163,184,0.10)',
                  }}
                >
                  <Typography variant="body2" sx={{ color: '#f9fafb', fontWeight: 600, minWidth: 120 }}>
                    {r.started
                      ? r.started.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
                      : '—'}
                  </Typography>
                  <Chip
                    size="small"
                    label={m.label}
                    sx={{ height: 20, fontSize: 11, fontWeight: 700, bgcolor: m.bg, color: m.color }}
                  />
                  <Typography variant="caption" sx={{ color: '#94a3b8', ml: 'auto' }}>
                    {r.groupsDone || 0}/{r.groupsTotal || '?'} groups · {r.commentsScraped || 0} comments
                    {r.commentsGood != null && (
                      <>
                        {' · '}
                        <Box component="span" sx={{ color: '#86efac' }}>{r.commentsGood} good</Box>
                      </>
                    )}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        )}

        {loading && !error && (
          <Box sx={{ mb: 2 }}>
            <LinearProgress
              sx={{
                height: 3,
                borderRadius: 9999,
                bgcolor: 'rgba(15,23,42,0.8)',
                '& .MuiLinearProgress-bar': {
                  background: 'linear-gradient(90deg, #22c55e, #4ade80, #22c55e)',
                },
              }}
            />
          </Box>
        )}

        {error && (
          <Typography color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}

        {/* Buyer feed */}
        <Paper elevation={0} sx={feedPaperSx}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'baseline',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 1,
              mb: 1,
            }}
          >
            <Typography variant="subtitle1" fontWeight={700} sx={{ letterSpacing: 0.5, color: '#ffffff' }}>
              {view === 'top' ? `Highest scoring · ${presetLabel}` : `Latest approved · ${statLabel}`}
              {tier !== 'all' ? ` · ${TIER_META[tier].label}` : ''}
            </Typography>
            <Typography variant="caption" sx={{ color: '#94a3b8' }}>
              {view === 'top' ? 'One row per buyer, best comment first' : 'Newest comment first · only comments the AI approved'}
              {unvetted > 0 ? ` · ${unvetted} waiting for AI` : ''}
            </Typography>
          </Box>

          {loading ? (
            <Typography variant="body2" sx={{ color: '#f9fafb' }}>
              Loading…
            </Typography>
          ) : (
            <BuyerList
              buyers={shown}
              visible={visible}
              setVisible={setVisible}
              allComments={comments}
              emptyText={
                view === 'top'
                  ? `No ranked buyers for ${presetLabel.toLowerCase()}.`
                  : `No ${tier === 'all' ? 'approved' : TIER_META[tier].label} buyers for ${label}.`
              }
            />
          )}
        </Paper>
      </Box>
    </Box>
  );
}
